import type { RemoteConfig } from '@paid-ui/models/app-config';
import type { Product } from '@paid-ui/models/product';
import type { UserAccount, UserDecoded, UserDetail } from '@paid-ui/types/parties';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnapshot } from 'valtio/react';

import { BackgroundCheckStatus } from '@paid-ui/enums/account';
import { useIsomorphicEffect } from '@paid-ui/hooks/use-isomorphic-effect';
import { saveRemoteConfig } from '@paid-ui/models/app-config';
import { saveDefaultFeatures } from '@paid-ui/models/feature';
import { saveProducts } from '@paid-ui/models/product';
import {
  saveConsents,
  saveProfile,
  saveTransactionAccounts,
  signIn,
  userManager,
} from '@paid-ui/models/user';
import { saveUserGroup } from '@paid-ui/models/user-group';
import {
  saveCachedTransactionAccounts,
  transactionAccountsQueryKey,
} from '@paid-ui/queries/transaction-account';
import { useToast } from '@paid-ui/ui/toast';
import { request } from '@paid-ui/utils/axios';
import { nowMel } from '@paid-ui/utils/datetime';
import { reportAppError } from '@paid-ui/utils/error-report';

const handleTokenEvent = (event: StorageEvent) => {
  if (!event.newValue) {
    return;
  }
  if (event.key === 'get_session') {
    globalThis.localStorage.setItem(
      'access_token',
      globalThis.sessionStorage?.getItem('access_token') || '',
    );
    globalThis.localStorage.removeItem('access_token');
  }
  if (event.key === 'access_token') {
    globalThis.sessionStorage.setItem('access_token', event?.newValue || '');
    globalThis.localStorage.removeItem('get_session');
  }
};

type GetAppConfigResponse = {
  data: RemoteConfig;
} | null;

type GetProductListResponse = {
  data: Product[];
} | null;

type GetUserProfileResponse = {
  data: UserDetail;
} | null;

type GetTransactionAccountsResponse = {
  data: UserAccount[];
} | null;

export const useGetUserProfile = () => {
  const toast = useToast();
  const router = useRouter();
  const client = useQueryClient();
  const mountedRef = useRef(false);
  const [userId, setUserId] = useState('');
  const { forceUpdate, consents, impersonation } = useSnapshot(userManager);
  const hasUnacceptedConsent = consents.some((entry) => !entry.isUserAccepted);

  useEffect(() => {
    if (!router.isReady || impersonation) return;
    if (!hasUnacceptedConsent) return;
    if (router.pathname === '/consents') return;
    const currentUrl = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
    const params = new URLSearchParams({ next: currentUrl });
    router.replace(`/consents?${params.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnacceptedConsent, impersonation, router.pathname]);

  const redirectToSignIn = (message?: string) => {
    const urlSearchParams = new URLSearchParams(globalThis.location.search);
    urlSearchParams.set('next', globalThis.location.pathname);
    urlSearchParams.sort();
    router.replace(`/signin?${urlSearchParams.toString()}`);
    sessionStorage.removeItem('access_token');
    if (message) {
      toast.error(message);
    }
  };

  const configQuery = useQuery({
    enabled: Boolean(userId),
    queryKey: ['GetAppConfig'],
    queryFn: async ({ signal }) => {
      const { data } = await request.get<GetAppConfigResponse>('/app/config', {
        signal,
      });
      return data?.data;
    },
    onSuccess(data) {
      if (!data) {
        redirectToSignIn('Failed to get app config.');
        return;
      }
      saveRemoteConfig(data);
      saveDefaultFeatures(data.features);
    },
    onError(error) {
      redirectToSignIn(error instanceof Error ? error.message : 'Failed to get app config.');
    },
  });

  const productsQuery = useQuery({
    enabled: Boolean(userId),
    queryKey: ['GetProductList'],
    queryFn: async ({ signal }) => {
      const { data } = await request.get<GetProductListResponse>('/products', {
        signal,
      });
      return data?.data ?? [];
    },
    onSuccess(data) {
      saveProducts(data);
    },
    // Without this the list simply stays empty on failure, and every screen that resolves a
    // contract to a product then reads that as "withdrawn" rather than "we never loaded it".
    onError(error) {
      reportAppError({
        type: 'UNKNOWN',
        code: 'UNKNOWN',
        description: 'Failed to load the product list',
        details: { message: error instanceof Error ? error.message : String(error) },
      });
    },
  });

  const transactionAccountsQuery = useQuery({
    enabled: false,
    queryKey: transactionAccountsQueryKey,
    queryFn: async ({ signal }) => {
      const { data } = await request.get<GetTransactionAccountsResponse>('/transaction-account', {
        signal,
      });
      return data?.data ?? [];
    },
    onSuccess(data) {
      if (data) {
        saveTransactionAccounts(data);
      } else {
        toast.error('Failed to get transaction accounts.');
      }
    },
    onError(error) {
      toast.error(error instanceof Error ? error.message : 'Failed to get transaction accounts.');
    },
  });

  const profileQuery = useQuery({
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: configQuery.isSuccess && Boolean(userId),
    queryKey: ['GetUserProfile', userId],
    queryFn: async ({ signal }) => {
      const { data } = await request.get<GetUserProfileResponse>(`/users/${userId}`, {
        signal,
      });
      return data?.data;
    },
    onSuccess(data) {
      if (!data) {
        redirectToSignIn('Failed to get user profile.');
        return;
      }

      const profile = {
        ...data,
        displayName: `${data.firstName} ${data.lastName}`,
      };

      saveProfile(profile);
      saveUserGroup(profile);
      if (!profile.impersonation) {
        saveConsents(profile.consents, configQuery.data?.consentItems);
      }
      if (profile.backgroundCheck?.status === BackgroundCheckStatus.PASS) {
        transactionAccountsQuery.refetch();
      } else {
        // The accounts page fetches the list itself for a user without a passing
        // check, sharing this query's cache. Reuse whatever it found so the poll
        // doesn't clear it every minute — but read the cache rather than this
        // hook's own result, which is a render behind whenever something else
        // refetched the list, and would put the pre-refetch list back.
        saveCachedTransactionAccounts(client);
      }
    },
    onError(error) {
      redirectToSignIn(error instanceof Error ? error.message : 'Failed to get user profile.');
    },
  });

  useIsomorphicEffect(() => {
    const searchParams = new URLSearchParams(globalThis.location.search);
    let token = searchParams.get('access_token');
    const impersonation = globalThis.sessionStorage.getItem('impersonation') === '1';

    if (token) {
      globalThis.sessionStorage.setItem('access_token', token);
      globalThis.sessionStorage.setItem('impersonation', '1');

      searchParams.delete('access_token');
      searchParams.sort();
      router.replace(
        {
          pathname: globalThis.location.pathname,
          search: searchParams.toString(),
        },
        undefined,
        {
          shallow: true,
        },
      );
    } else if (!impersonation) {
      globalThis.sessionStorage.removeItem('impersonation');
      globalThis.addEventListener('storage', handleTokenEvent);
      const newEvent = new StorageEvent('storage');
      globalThis.dispatchEvent(newEvent);
      token = globalThis.sessionStorage.getItem('access_token');
      if (!token) {
        localStorage.setItem('get_session', nowMel().valueOf().toString());
      }
    }

    const timer = setTimeout(() => {
      // Retrieve the token from the session storage
      // 1. Token saved from search params
      // 2. Get session event dispatch, get token from another tab
      token = globalThis.sessionStorage.getItem('access_token');

      if (!token) {
        redirectToSignIn();
        return;
      }

      signIn(token);
      const [, encodedData] = token.split('.');

      if (!encodedData) {
        redirectToSignIn();
        return;
      }

      try {
        const user = JSON.parse(globalThis.atob(encodedData)) as UserDecoded;
        if (!user) {
          redirectToSignIn('Failed to decode user data.');
          return;
        }
        setUserId(user.username);
        mountedRef.current = true;
      } catch {
        redirectToSignIn('Failed to decode user data.');
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      globalThis.removeEventListener('storage', handleTokenEvent);
    };
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;
    profileQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdate]);

  return profileQuery;
};
